// components/MemberPortal/ChatWindow.js
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../axiosConfig';

const ChatWindow = ({
  playRequest,
  currentUserId,
  onClose,
  onScheduleRound,
  onDenyRequest,
  onUpdate
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const isHost = currentUserId && playRequest?.host?.user?.id === currentUserId;
  const canSchedule = isHost && playRequest?.status === 'pending';
  const canConfirm = !isHost && playRequest?.status === 'scheduled';

  useEffect(() => {
    loadChatData();
  }, [playRequest?.request_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChatData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/member-portal/chats/${playRequest.request_id}/`);
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axiosInstance.post(
        `/member-portal/chats/${playRequest.request_id}/send/`,
        { content: newMessage }
      );

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const confirmRound = async () => {
    try {
      await axiosInstance.post(`/member-portal/play-requests/${playRequest.request_id}/confirm/`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error confirming round:', error);
      alert('Error confirming round. Please try again.');
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-window-overlay">
      <div className="chat-window">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-title">
            <h3>
              {playRequest?.target_club?.name || 'Golf Round Chat'}
            </h3>
            <p>
              {isHost ? 'Guest Request from' : 'Host Communication with'} {' '}
              {otherUser?.first_name || otherUser?.username || 'Member'}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Host Action Banner */}
        {(canSchedule || canConfirm) && (
          <div className="action-banner">
            {canSchedule && (
              <>
                <div className="banner-message">
                  <span className="banner-icon">🏌️</span>
                  <span>Ready to host this member? Schedule the round or politely decline.</span>
                </div>
                <div className="banner-actions">
                  <button className="schedule-button" onClick={onScheduleRound}>
                    Schedule Round
                  </button>
                  <button
                    className="deny-button"
                    onClick={() => onDenyRequest('Thank you for your interest, but I\'m not available for hosting at this time.')}
                  >
                    Deny
                  </button>
                </div>
              </>
            )}

            {canConfirm && (
              <>
                <div className="banner-message">
                  <span className="banner-icon">✅</span>
                  <span>Round scheduled! Please confirm if you can make it.</span>
                </div>
                <div className="banner-actions">
                  <button className="confirm-button" onClick={confirmRound}>
                    Confirm Round
                  </button>
                  <button className="cancel-button" onClick={() => onDenyRequest('I need to cancel this round.')}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Request Details */}
        <div className="request-summary">
          <div className="summary-item">
            <strong>Status:</strong>
            <span className={`status ${playRequest?.status}`}>{playRequest?.status}</span>
          </div>
          {playRequest?.preferred_date && (
            <div className="summary-item">
              <strong>Preferred Date:</strong> {new Date(playRequest.preferred_date).toLocaleDateString()}
            </div>
          )}
          {playRequest?.scheduled_date && (
            <div className="summary-item">
              <strong>Scheduled:</strong> {new Date(playRequest.scheduled_date).toLocaleDateString()}
              {playRequest?.scheduled_time && ` at ${playRequest.scheduled_time}`}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="messages-container">
          {loading ? (
            <div className="loading-messages">Loading conversation...</div>
          ) : messages.length === 0 ? (
            <div className="no-messages">
              <p>Start the conversation!</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message, index) => {
                const isFromCurrentUser = message.sender === currentUserId;
                return (
                  <div
                    key={message.id || index}
                    className={`message ${isFromCurrentUser ? 'from-me' : 'from-other'}`}
                  >
                    <div className="message-content">
                      <p>{message.content}</p>
                      <span className="message-time">
                        {formatMessageTime(message.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <form className="message-input-form" onSubmit={sendMessage}>
          <div className="input-container">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={sending}
              className="message-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="send-button"
            >
              {sending ? '...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;